import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, IdCard, Pencil, X } from "lucide-react";
import { updatePersonalCode } from "services/personnel.service";
import { SensitivePersonalCode } from "components/sensitive-amount/SensitiveText";
import { errorCode } from "features/positions/positions-errors";

interface Props {
  companyId: string;
  userId: string;
  /** null = not recorded. */
  value: string | null;
  /** viewerCan.editPersonalCode — Master / 'Access onboarding'. */
  canEdit: boolean;
  /** Called with the stored code after a successful save, so the card shows it. */
  onSaved: (personalCode: string | null) => void;
}

/**
 * «Asmens kodas» in the card header: masked behind an eye like a salary; an
 * editor gets a pencil that turns it into an input. The backend validates the
 * code (format + check digit); an empty value clears it (foreigners).
 */
export default function PersonalCodeField({ companyId, userId, value, canEdit, onSaved }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(value || "");
    setError(null);
  };
  const cancelEdit = () => {
    setDraft(null);
    setError(null);
  };
  const save = async () => {
    // Enter while a save is in flight must not send a second one.
    if (draft === null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await updatePersonalCode(companyId, userId, draft.trim() || null);
      setDraft(null);
      onSaved(saved);
    } catch (e) {
      setError(t(errorCode(e) === "INVALID_PERSONAL_CODE" ? "personal_code_invalid" : "personal_code_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const label = (
    <>
      <IdCard className="h-3.5 w-3.5" aria-hidden />
      {t("personal_code")}:
    </>
  );

  if (draft === null) {
    return (
      <span className="inline-flex items-center gap-1">
        {label}
        <SensitivePersonalCode value={value} />
        {canEdit && (
          <button
            type="button"
            onClick={startEdit}
            aria-label={t("edit_personal_code")}
            title={t("edit_personal_code")}
            className="text-gray-400 hover:text-gray-700 max-md:p-[15px]"
          >
            <Pencil size={14} />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {label}
      <input
        type="text"
        inputMode="numeric"
        maxLength={11}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") cancelEdit();
        }}
        aria-label={t("personal_code")}
        aria-invalid={!!error}
        autoFocus
        className="w-32 rounded border border-gray-300 px-2 py-0.5 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 max-md:py-2 md:text-sm"
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        aria-label={t("save")}
        title={t("save")}
        className="text-green-700 hover:text-green-900 disabled:opacity-50 max-md:p-3.5"
      >
        <Check size={16} />
      </button>
      <button
        type="button"
        onClick={cancelEdit}
        disabled={saving}
        aria-label={t("cancel")}
        title={t("cancel")}
        className="text-gray-400 hover:text-gray-700 disabled:opacity-50 max-md:p-3.5"
      >
        <X size={16} />
      </button>
      {error && (
        <span role="alert" className="text-red-700">
          {error}
        </span>
      )}
    </span>
  );
}
